import { computed, Injector, signal } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  forkJoin,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { moveItemInArray } from '@angular/cdk/drag-drop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigGeralModel } from '@shared/configuracao/configuracao.model';
import { ConfiguracaoService } from '@shared/configuracao/configuracao.service';
import { CriterioModel, CriterioReadService, CriterioTipoDado } from '@shared/criterio';
import { DateTime } from 'luxon';
import {
  DataManagerColunaConfig,
  DataManagerDadoAdicionalConfig,
  DataManagerFiltroConfig,
  DataManagerPreferenciaUsuario,
} from '../contracts/data-manager-config.model';
import {
  DataManagerDefinicaoColunaCalculada,
  DataManagerDefinicaoColunaLookup,
  DataManagerDefinicaoColunaSimples,
} from '../contracts/data-manager-definicao.model';
import { DataManagerProvider, DataManagerProviderLookupConfig } from '../contracts/data-manager-provider';
import {
  DataManagerColuna,
  DataManagerColunaCalculada,
  DataManagerColunaCriterio,
  DataManagerColunaDadoAdicional,
  DataManagerColunaGrupo,
  DataManagerColunaSimples,
  DataManagerContent,
  DataManagerOrdenacaoSentido,
} from '../contracts/data-manager.model';
import { DataManagerFiltros } from '../state/data-manager-filtro';
import { DataManagerGrupo } from '../state/data-manager-grupo';
import { DataManagerProxy } from '../state/data-manager-proxy';

export type DataManagerTipoController =
  | 'analise-calculo'
  | 'apuracao'
  | 'autorizacao'
  | 'dashboard-card'
  | 'demonstrativo-comissionado-vendas'
  | 'demonstrativo-comissionado-autorizacoes'
  | 'historico';

type DataManagerControllerConfig = {
  carregarCriterios: boolean;
  nivelUnico: boolean;
  semFeedback: boolean;
  semLoader: boolean;
};

const DataManagerControllerTipoConfig: {
  [key in DataManagerTipoController]: DataManagerControllerConfig;
} = {
  'analise-calculo': {
    carregarCriterios: true,
    nivelUnico: false,
    semFeedback: false,
    semLoader: false,
  },
  apuracao: {
    carregarCriterios: true,
    nivelUnico: false,
    semFeedback: false,
    semLoader: false,
  },
  autorizacao: {
    carregarCriterios: false,
    nivelUnico: false,
    semFeedback: false,
    semLoader: false,
  },
  'dashboard-card': {
    carregarCriterios: true,
    nivelUnico: true,
    semFeedback: true,
    semLoader: true,
  },
  'demonstrativo-comissionado-vendas': {
    carregarCriterios: true,
    nivelUnico: false,
    semFeedback: false,
    semLoader: false,
  },
  'demonstrativo-comissionado-autorizacoes': {
    carregarCriterios: true,
    nivelUnico: false,
    semFeedback: false,
    semLoader: false,
  },
  historico: {
    carregarCriterios: true,
    nivelUnico: false,
    semFeedback: false,
    semLoader: false,
  },
};

type DataManagerControllerLookupItem = {
  config: DataManagerProviderLookupConfig;
  dados: Map<string | number, any>;
};

type DataManagerGruposPorChave<T> = {
  subGrupos: Map<string | number, DataManagerGruposPorChave<T>>;
  grupo: DataManagerGrupo<T>;
};

export class DataManagerController<T> {
  // Private
  private _colunas = signal<DataManagerColuna[]>([]);
  private _colunasTotais = signal<DataManagerColunaGrupo[]>([]);
  private _conteudoOrdenado = signal<DataManagerContent<T>[]>([]);
  private _dados = signal<DataManagerProxy<T>[]>([]);
  private _conteudoVisivel = signal<DataManagerContent<T>[]>([]);
  private _dadosAlterados = true;
  private _dadosDisponiveis = signal<boolean>(false, { equal: () => false });
  // Filtro sempre habilitado no início
  private _filtrando = signal<boolean>(true);
  private _filtros = new DataManagerFiltros();
  private _grupos = signal<DataManagerGrupo<T>[]>([]);
  private _gruposColunas = signal<DataManagerColunaGrupo[]>([]);
  private _refreshNecessario = signal(false, { equal: () => false });
  private _scrollTop = 0;
  private configColunas?: DataManagerColunaConfig[];
  private configFiltros?: DataManagerFiltroConfig[];
  private configSvc: ConfiguracaoService;
  private controllerConfig: DataManagerControllerConfig;
  private criteriosMap = new Map<string, CriterioModel>();
  private criterioSvc: CriterioReadService;
  private daCache = new Map<string, Record<string, any>>();
  private dadosAdicionaisAtualizados = signal(false, { equal: () => false });
  private definicaoColunasCalculadas = new Map<string, DataManagerDefinicaoColunaCalculada>();
  private definicaoColunasLookup = new Map<string, DataManagerDefinicaoColunaLookup[]>();
  private definicaoColunasSimples = new Map<string, DataManagerDefinicaoColunaSimples>();
  private dmProvider?: DataManagerProvider<T>;
  private gruposPorChave = new Map<string | number, DataManagerGruposPorChave<T>>();
  private lookupCache = new Map<string, DataManagerControllerLookupItem>();
  private setupBasicoConcluido = false;
  private snackBar: MatSnackBar;

  // Subjects
  private _preferenciasChanged$ = new Subject<DataManagerPreferenciaUsuario>();
  private colunasConfiguradas$ = new BehaviorSubject(false);
  private configLoaded$ = new BehaviorSubject(false);
  private configGeralLoaded$!: Observable<ConfigGeralModel>;
  private criterioLoaded$ = new BehaviorSubject(false);
  private dadosAtualizados$ = new Subject();

  // Public
  colunas = computed(() => {
    return this._colunas();
  });
  colunasTotais = computed(() => {
    return this._colunasTotais();
  });
  /** O objetivo do contexto é manter dados que possam ser úteis para o componente
   *    durante o ciclo de vida do Controller
   */
  contexto = signal<any>(undefined);
  dados = computed(() => {
    return this._dados();
  });
  conteudoVisivel = computed(() => this._conteudoVisivel());
  conteudoVisivelDados = computed(() =>
    this._conteudoVisivel().filter((item): item is DataManagerProxy<T> => !item.isGrupo),
  );
  conteudoVisivelGrupos = computed(() =>
    this._conteudoVisivel().filter((item): item is DataManagerGrupo<T> => item.isGrupo),
  );
  dadosDisponiveis = computed(() => {
    if (this._dadosDisponiveis()) {
      return { scrollTop: this._scrollTop };
    } else {
      return null;
    }
  });
  filtrando = computed(() => this._filtrando());
  grupos = computed(() => this._grupos());
  gruposColunas = computed(() => this._gruposColunas());
  refreshNecessario = computed(() => this._refreshNecessario() && this.dadosAdicionaisAtualizados(), {
    equal: () => false,
  });

  constructor(
    private tipoController: DataManagerTipoController,
    private injector: Injector,
    private provider: DataManagerProvider<T>,
  ) {
    this.criterioSvc = injector.get(CriterioReadService);
    this.controllerConfig = DataManagerControllerTipoConfig[tipoController];
    this.snackBar = injector.get(MatSnackBar);
    this.configSvc = injector.get(ConfiguracaoService);
    this.dmProvider = provider;
  }

  get filtros() {
    return this._filtros;
  }

  get preferenciasChanged(): Observable<DataManagerPreferenciaUsuario> {
    return this._preferenciasChanged$.asObservable();
  }

  get niveisGrupo() {
    return this._gruposColunas().length;
  }

  get scrollTop() {
    return this._scrollTop;
  }

  setDadosAlterados(dadosAlterados = true) {
    this._dadosAlterados = dadosAlterados;
  }

  set scrollTop(scrollTop: number) {
    this._scrollTop = scrollTop;
  }

  /** Executa as inicializações necessárias */
  init() {
    if (!this.dmProvider) {
      throw new Error('Provider não fornecido. Favor executar setProvider antes de executar init.');
    }

    // Carrega configuração geral para obter limite de linhas
    this.configGeralLoaded$ = this.configSvc.getConfig<ConfigGeralModel>('geral', this.controllerConfig.semLoader).pipe(
      tap((configGeral) => {
        this._filtros.setFiltroConfig(configGeral.filtro);
      }),
      shareReplay(1),
    );
    this.configGeralLoaded$.subscribe();

    // Se os dados não foram alterados desde a última inicialização, apenas
    // sinaliza que as configurações já estão carregadas
    if (!this._dadosAlterados) {
      this.colunasConfiguradas$.next(true);
      this.configLoaded$.next(true);
      this.criterioLoaded$.next(true);
      this.configurarOrdenacao();
      this.atualizarConteudoVisivel();
      this._dadosDisponiveis.set(true);
      return;
    }

    // Se os dados foram alterados e já havia uma seleção é necessário
    //   atualizarmos esta seleção
    if (this._dados().length) {
      this.refresh();
    }

    if (!this.setupBasicoConcluido) {
      this.carregarCriterios();
      this.carregarConfig();
      this.configurarColunas();
      this.configurarFiltros();
      this.configurarColunasTotais();
      this.carregarDefinicoesColunas();
      this.carregarDadosAdicionais();
      this.setupBasicoConcluido = true;
    }

    this._dadosAlterados = false;
  }

  /// AINDA EM DESENVOLVIMENTO
  /** Atualiza 1 item, incluindo grupos e ordenação */
  atualizarItemUnico(item: T) {
    // Procurar proxy do objeto
    const id = (item as any).id;
    if (!id) {
      throw new Error('Proxy não tem propriedade id, não pode ser atualizado individualmente');
    }
    let proxy = this.dados().find((d) => d.id === id);
    if (!proxy) {
      proxy = new DataManagerProxy(item);
    }

    // Encontra grupo onde este item deve ser inserido/atualizado
    const grupo = this.getGrupoForProxy(proxy);
  }

  /** Move a coluna para depois da coluna informada em aposColuna */
  colunaAlterarOrdem(coluna: DataManagerColuna, aposColuna: DataManagerColuna) {
    const colunas = this._colunas();
    let indiceAtual = colunas.findIndex((c) => c === coluna);
    if (indiceAtual === -1) {
      return;
    }
    let indiceDestino = colunas.findIndex((c) => c === aposColuna);
    if (indiceDestino === -1) {
      return;
    }
    moveItemInArray(colunas, indiceAtual, indiceDestino);

    // Se for um critério move os dados adicionais junto
    if (coluna.tipoColuna === 'criterio' || coluna.tipoColuna === 'simples') {
      const desl = indiceAtual > indiceDestino ? 1 : 0;
      indiceAtual += desl;
      let colunaDa = colunas[indiceAtual];
      while (colunaDa && colunaDa.tipoColuna === 'dadoAdicional' && colunaDa.colunaMaster.id === coluna.id) {
        indiceDestino += desl;
        moveItemInArray(colunas, indiceAtual, indiceDestino);
        indiceAtual += desl;
        colunaDa = colunas[indiceAtual];
      }
    }

    this._colunas.set([...colunas]);
    this.configurarColunasTotais();
    this.emitConfigChanged();
  }

  /** Dado adicional foi adicionado ou excluído */
  dadoAdicionalSelecionado(coluna: DataManagerColunaDadoAdicional) {
    // Se o critério associado não estiver agrupado precisamos atualizar a lista de colunas
    //   para a tabela incluir/excluir a coluna deste dado adicional
    if (!coluna.colunaMaster.config.grupo) {
      this._colunas.set([...this._colunas()]);
    }
    this.emitConfigChanged();
  }

  colunaAlterarLargura(coluna: DataManagerColuna, largura: number) {
    const larguraNormalizada = Math.max(6, Math.round(largura * 10) / 10);
    if (!Number.isFinite(larguraNormalizada)) {
      return;
    }

    if (coluna.config.largura === larguraNormalizada) {
      return;
    }

    coluna.config.largura = larguraNormalizada;
    this._colunas.set([...this._colunas()]);
    this.emitConfigChanged();
  }

  filtroHabilitar() {
    this._filtrando.set(true);
  }

  filtroCancelar() {
    this._filtros.resetAlteracoes(false);
    this._filtrando.set(false);
  }

  grupoAdicionar(coluna: DataManagerColunaGrupo) {
    // Recupera último grupo
    const ultGrupo = this._colunas().reduce((prev, curr) => {
      if (curr.tipoColuna !== 'dadoAdicional' && (curr.config.grupo || 0) > prev) {
        prev = curr.config.grupo!;
      }
      return prev;
    }, 0);
    coluna.config.grupo = ultGrupo + 1;

    this.configurarGrupos();
    this.configurarOrdenacao();
    this.configurarColunasTotais();
    this._colunas.set([...this._colunas()]);
    this.emitConfigChanged();
  }

  /** Alterna o status de expansão de um grupo */
  grupoAlternarExpandir(grupo: DataManagerGrupo<T>) {
    grupo.expandido = !grupo.expandido;
    this.atualizarConteudoVisivel();
  }

  /** Expande/retrai um nível */
  grupoExpandirRetrair(grupo: DataManagerGrupo<T>, expandir: boolean) {
    grupo.expandido = expandir;
    this.atualizarConteudoVisivel();
  }

  /** Expande/retrai um nível inteiro */
  grupoExpandirRetrairTudo(coluna: DataManagerColunaGrupo, expandir: boolean) {
    this._grupos().forEach((grupo) => {
      const colunaGrupo = grupo.coluna;
      if (colunaGrupo && colunaGrupo.id === coluna.id) {
        grupo.expandido = expandir;
      }
    });

    this.atualizarConteudoVisivel();
  }

  grupoMover(coluna: DataManagerColunaGrupo, indiceDestino: number) {
    if (coluna.config.grupo === undefined) {
      return;
    }

    // Ajusta numeração dos grupos
    const grupo = coluna.config.grupo;
    const inicioAjuste = grupo < indiceDestino ? grupo + 1 : indiceDestino;
    const fimAjuste = grupo > indiceDestino ? grupo - 1 : indiceDestino;
    const ajuste = grupo > indiceDestino ? 1 : -1;
    for (const item of this._colunas()) {
      const colGrupo = item as DataManagerColunaGrupo;
      const colGrupoConfig = colGrupo.config;
      if (colGrupoConfig.grupo !== undefined) {
        if (colGrupoConfig.grupo >= inicioAjuste && colGrupoConfig.grupo <= fimAjuste) {
          colGrupoConfig.grupo += ajuste;
        }
      }
    }
    coluna.config.grupo = indiceDestino;

    this.configurarGrupos(true);
    this.configurarOrdenacao();
    this.emitConfigChanged();
  }

  grupoRemover(coluna: DataManagerColunaGrupo) {
    // Ajusta números dos grupos para garantir a sequencia
    this._colunas().forEach((col) => {
      if (col.tipoColuna !== 'dadoAdicional') {
        if ((col.config.grupo || 0) > coluna.config.grupo!) {
          col.config.grupo!--;
        }
      }
    });

    delete coluna?.config.grupo;

    this.configurarGrupos(true);
    this.configurarOrdenacao();
    this.configurarColunasTotais();
    this._colunas.set([...this._colunas()]);
    this.emitConfigChanged();
  }

  toggleOcultarColuna(coluna: DataManagerColunaGrupo, ocultar: boolean) {
    coluna.config.ocultarColuna = ocultar;
    this._colunas.set([...this._colunas()]);
    this.emitConfigChanged();
  }

  /** Configura e dispara a execução da ordenação  */
  ordenar(coluna: DataManagerColuna, sentido?: DataManagerOrdenacaoSentido) {
    const colunas = this._colunas();
    if (!colunas) {
      return;
    }

    // Limpa ordenação anterior se houver
    colunas.forEach((col) => {
      if (col.config.ordenacao) {
        delete col.config.ordenacao;
      }
    });
    coluna.config.ordenacao = sentido;

    this._colunas.set(colunas);
    this.configurarOrdenacao();
    this.emitConfigChanged();
  }

  /** Executa uma atualização geral dos dados */
  refresh() {
    this.dadosAtualizados$.pipe(take(1)).subscribe({
      next: () => {
        if (this._dados().length > 0) {
          this.abrirSnackbar('Dados atualizados com sucesso');
          this._filtros.resetAlteracoes(true);
          this._filtrando.set(false);
        } else {
          this.abrirSnackbar('Nenhum dado encontrado com os filtros informados');
        }
      },
    });
    this.carregarDados();
    this.carregarLookups(true);
    this._dadosAlterados = false;
  }

  /** Limpa as preferências do usuáro */
  resetPreferencias() {
    this.dadosAtualizados$.pipe(take(1)).subscribe({
      next: () => {
        this.abrirSnackbar('Preferências do usuário reinicializadas com sucesso');
      },
    });

    this.dmProvider?.resetPreferencias().subscribe({
      next: () => {
        this.configLoaded$.next(false);
        this.colunasConfiguradas$.next(false);
        this.carregarConfig();
        this.configurarColunas();
        this.configurarColunasTotais();
        this.configurarGrupos(true);
      },
    });
  }

  /** Configura uma coluna como totalizável ou não */
  totalizar(coluna: DataManagerColunaGrupo) {
    coluna.config.total = !coluna.config.total;

    if (coluna.config.total) {
      // Zera totais dessa coluna nos grupos
      this._grupos().forEach((grupo) => grupo.totalReset(coluna));
      // Recalcula totais dessa coluna
      this._dados().forEach((dado) => dado.totalizarColuna(coluna));
    }
    this.configurarColunasTotais();

    this.emitConfigChanged();
  }

  /** Snackbar para mensagens rápidas */
  private abrirSnackbar(texto: string) {
    if (!this.controllerConfig.semFeedback) {
      this.snackBar.open(texto, 'FECHAR', {
        duration: 5000,
        horizontalPosition: 'right',
      });
    }
  }

  private buildColunaConfig(
    colunas: {
      id: string;
      dadosAdicionais?: DataManagerDadoAdicionalConfig[];
    }[],
  ) {
    const novasColunas: DataManagerColuna[] = [];
    const nivelUnico = this.controllerConfig.nivelUnico;

    colunas.forEach((colunaConfig) => {
      // Coluna simples
      const campoComum = this.definicaoColunasSimples.get(colunaConfig.id);

      if (campoComum) {
        // Um campos simples também pode, opcionalmente, utilizar lookup também
        let campoSimplesLookup = {};
        if (campoComum.idLookup && campoComum.atributoLookup) {
          const lookupConfig = this.lookupCache.get(campoComum.idLookup);
          if (!lookupConfig) {
            throw new Error('Configuração de lookup não encontrada');
          }
          campoSimplesLookup = {
            lookup: (dadosOriginais: any) => this.getLookup(lookupConfig, dadosOriginais, campoComum.atributoLookup!),
          };
        }

        const path = nivelUnico ? [colunaConfig.id] : colunaConfig.id.split('.');
        const colSimples = {
          id: colunaConfig.id,
          path,
          tipoColuna: 'simples',
          descricao: campoComum.descricao,
          tooltip: campoComum.tooltip,
          tipoDado: campoComum.tipo,
          templateDados: campoComum.templateDados,
          templateGrupo: campoComum.templateGrupo,
          config: colunaConfig,
          dadosAdicionais: [],
          ...campoSimplesLookup,
        } as DataManagerColunaSimples;
        novasColunas.push(colSimples);

        // Carrega colunas lookup
        const colsLookup = this.definicaoColunasLookup.get(colunaConfig.id);
        colsLookup?.forEach((colLookup) => {
          const lookupConfig = this.lookupCache.get(colLookup.idLookup);
          if (!lookupConfig) {
            throw new Error('Configuração de lookup não encontrada');
          }

          const lookupColunaConfig = colunaConfig.dadosAdicionais?.find(
            (lookupConfig) => lookupConfig.id === colLookup.id,
          );

          const novaColunaLookup = {
            ...colLookup,
            id: `${colSimples.id}.${colLookup.id}`,
            path,
            tipoDado: colLookup.tipo,
            colunaMaster: colSimples,
            tipoColuna: 'dadoAdicional',
            sel: !!lookupColunaConfig,
            config: {
              id: colLookup.id,
              ordenacao: lookupColunaConfig?.ordenacao,
            },
            lookup: (key: string | number) => this.getLookup(lookupConfig, key, colLookup.atributoLookup),
          } as DataManagerColunaDadoAdicional;
          colSimples.dadosAdicionais.push(novaColunaLookup);
          novasColunas.push(novaColunaLookup);
        });

        return;
      }

      // Coluna calculada
      const colunaCalculada = this.definicaoColunasCalculadas.get(colunaConfig.id);
      if (colunaCalculada) {
        return novasColunas.push({
          ...colunaCalculada,
          tipoDado: colunaCalculada.tipo,
          tipoColuna: 'calculada',
          config: colunaConfig,
        } as DataManagerColunaCalculada);
      }

      // Configura coluna do tipo critério
      const path = colunaConfig.id.split('.');
      const nomeTecnicoCriterio = path[path.length - 1];
      const criterio = this.criteriosMap.get(nomeTecnicoCriterio);
      if (criterio) {
        const novaColunaCriterio: DataManagerColunaCriterio = {
          id: colunaConfig.id,
          path: nivelUnico ? [colunaConfig.id] : path,
          nomeTecnico: nomeTecnicoCriterio,
          tipoColuna: 'criterio',
          descricao: criterio.nome,
          tipoDado: criterio.tipoDado,
          config: colunaConfig,
          dadosAdicionais: [],
        };
        novasColunas.push(novaColunaCriterio);
        criterio.dadosAdicionais?.forEach((daCrit) => {
          // Configura array de dados adicionais dentro da coluna de critério
          const configDa = colunaConfig.dadosAdicionais?.find((daConfig) => daConfig.id === daCrit.nomeTecnico);

          // Cria colunas de dados adicionais
          const novaColunaAdicional = {
            id: `${colunaConfig.id}.${daCrit.nomeTecnico}`,
            tipoColuna: 'dadoAdicional',
            path: nivelUnico ? [colunaConfig.id] : path,
            descricao: daCrit.nome,
            tipoDado: daCrit.tipoDado,
            nomeTecnico: criterio.nomeTecnico,
            dadoAdicional: daCrit.nomeTecnico,
            config: {
              id: daCrit.nomeTecnico,
              ordenacao: configDa?.ordenacao,
            },
            sel: !!configDa,
            colunaMaster: novaColunaCriterio,
            lookup: (key) => this.getDadoAdicional(key, criterio.nomeTecnico, daCrit.nomeTecnico),
          } as DataManagerColunaDadoAdicional;
          novasColunas.push(novaColunaAdicional);
          novaColunaCriterio.dadosAdicionais.push(novaColunaAdicional);
        });
      }
    });
    return novasColunas;
  }

  /** Executa o config provider, recupera a configuração e sinaliza que a configuração foi carregada
   */
  private carregarConfig() {
    const dmProvider = this.dmProvider!;
    forkJoin([dmProvider.getConfig(), dmProvider.getPreferencias()]).subscribe({
      next: ([config, preferencias]) => {
        if (preferencias) {
          this.configColunas = preferencias.colunas;
        } else {
          // Se não existirem preferências ainda, cria preferências default
          //   com base na lista de colunas da config padrão
          this.configColunas = config.colunas.reduce((prev, curr) => {
            prev.push({
              id: curr.id,
              dadosAdicionais: curr.dadosAdicionais?.map((da) => ({
                id: da,
              })),
            });
            return prev;
          }, [] as DataManagerColunaConfig[]);
        }
        this.configFiltros = config.filtros;

        this.carregarLookups();
        this.configLoaded$.next(true);
      },
    });
  }

  /** Carrega os critérios ativos, cria map de critérios e sinaliza que os
   * critérios foram carregados
   */
  private carregarCriterios() {
    if (this.controllerConfig.carregarCriterios) {
      this.criterioSvc.getLista({ ativo: true }, this.controllerConfig.semLoader).subscribe((criterios) => {
        this.criteriosMap = new Map(criterios.map((crit) => [crit.nomeTecnico, crit]));
        this.criterioLoaded$.next(true);
      });
    } else {
      // Se os critérios não são necessários já sinaliza como carregados
      this.criterioLoaded$.next(true);
    }
  }

  /** Executa o provider para obter os dados */
  private carregarDados() {
    if (!this.configGeralLoaded$) {
      throw new Error('DataManager não inicializado. Executar init() antes de carregarDados().');
    }

    this.configGeralLoaded$
      .pipe(
        switchMap((configGeral) => {
          const filtro = this._filtros.filtroString;
          return this.dmProvider!.getDados(filtro, configGeral.filtro.limiteLinhas || 0);
        }),
        tap({
          next: (dados) => {
            const dadosProxy = dados.map((d) => new DataManagerProxy(d));
            this._dados.set(dadosProxy);
          },
        }),
        take(1),
      )
      .subscribe({
        next: () => {
          this.configurarGrupos();
        },
      });
  }

  /** Carrega dados adicionais dos critérios utilizados na configuração */
  private carregarDadosAdicionais() {
    combineLatest([this.configLoaded$, this.criterioLoaded$])
      .pipe(
        filter(([a, b]) => a && b),
        take(1),
      )
      .subscribe({
        next: () => {
          if (!this.configColunas || this.configColunas.length === 0 || this.criteriosMap.size === 0) {
            this.dadosAdicionaisAtualizados.set(true);
            return;
          }

          const dadosAdicionaisToLoad = this.configColunas.reduce(
            (prev, curr) => {
              const path = curr.id.split('.');
              const nomeTecnico = path[path.length - 1];
              const criterio = this.criteriosMap.get(nomeTecnico);
              if (criterio && (criterio.dadosAdicionais?.length || 0) > 0 && !this.daCache.has(nomeTecnico!)) {
                prev.push(
                  this.criterioSvc.getDadosAdicionais(nomeTecnico, this.controllerConfig.semLoader).pipe(
                    tap((da) => {
                      const daObj = da.reduce((prev, curr) => {
                        const id = curr['id'];
                        prev[id] = curr;
                        return prev;
                      }, {});
                      this.daCache.set(nomeTecnico, daObj);
                      this.dadosAdicionaisAtualizados.set(true);
                      // this._refreshNecessario.next(true);
                      this._filtros.setFiltroOptions(curr.id, da);
                    }),
                  ),
                );
              }
              return prev;
            },
            [] as Observable<Record<string, any>[]>[],
          );

          if (dadosAdicionaisToLoad.length === 0) {
            this.dadosAdicionaisAtualizados.set(true);
            return;
          }

          return forkJoin(dadosAdicionaisToLoad).subscribe({});
        },
      });
  }

  /** Obtem as definições das colunas calculadas e simples a partir do provider */
  private carregarDefinicoesColunas() {
    const definicoes = this.dmProvider?.getDefinicaoColunas();

    if (definicoes?.colunasSimples) {
      this.definicaoColunasSimples = new Map(definicoes.colunasSimples.map((cs) => [cs.id, cs]));
    } else {
      this.definicaoColunasSimples = new Map();
    }

    if (definicoes?.colunasCalculadas) {
      this.definicaoColunasCalculadas = new Map(definicoes.colunasCalculadas.map((cc) => [cc.id, cc]));
    } else {
      this.definicaoColunasCalculadas = new Map();
    }

    this.definicaoColunasLookup = new Map();
    if (definicoes?.colunasLookup) {
      definicoes.colunasLookup.forEach((col) => {
        const cols = this.definicaoColunasLookup.get(col.idColunaMaster);
        if (cols) {
          return cols.push(col);
        }
        this.definicaoColunasLookup.set(col.idColunaMaster, [col]);
      });
    }
  }

  /** Carrega os dados para as colunas de lookup */
  private carregarLookups(force: boolean = false) {
    const lookupConfig = this.dmProvider?.getLookupConfig && this.dmProvider?.getLookupConfig();
    if (lookupConfig && lookupConfig.length > 0) {
      lookupConfig.forEach((lc) => {
        let lookup = this.lookupCache.get(lc.id);
        if (!force && lookup) {
          return;
        }

        const lookupDados = lookup?.dados || new Map();
        this.lookupCache.set(
          lc.id,
          lookup || {
            config: lc,
            dados: lookupDados,
          },
        );

        // Os dados do lookup só podem ser carregados após as colunas terem sido configuradas
        const lookupObservable = combineLatest([this.colunasConfiguradas$, lc.dados()]);

        lookupObservable.subscribe({
          next: ([colunasOk, dados]) => {
            if (!colunasOk) {
              return;
            }

            dados.forEach((item) => {
              lookupDados.set(item[lc.campoChaveLookup || 'id'], item);
            });
            this._refreshNecessario.set(true);

            // Envia dados do lookup para os filtros de colunas que utilizam esse lookup
            this.definicaoColunasSimples.forEach((cs) => {
              if (cs.idLookup === lc.id) {
                this._filtros.setFiltroOptions(cs.id, dados, cs.atributoLookup);
              }
            });
          },
        });
      });
    }
  }

  /** Cria as configurações das colunas da tabela, de acordo com a configuração
   * recebida
   */
  private configurarColunas() {
    // Aguarda os critérios e configurações serem carregadas
    combineLatest([this.configLoaded$, this.criterioLoaded$])
      .pipe(
        filter(([a, b]) => a && b),
        take(1),
      )
      .subscribe({
        next: ([]) => {
          if (!this.configColunas) {
            return;
          }

          this._colunas.set(this.buildColunaConfig(this.configColunas));

          this.colunasConfiguradas$.next(true);
        },
      });
  }

  private configurarFiltros() {
    this.colunasConfiguradas$
      .pipe(
        filter((ok) => ok),
        take(1),
      )
      .subscribe({
        next: () => {
          const dmProvider = this.dmProvider!;

          const getFiltroConfig = dmProvider.getFiltroConfig?.bind(dmProvider) || (() => ({}));

          this._filtros.setColunasFiltro(
            this.configFiltros?.map((f) => ({
              ...f,
              ...getFiltroConfig(f.id),
            })) || [],
          );

          this._filtros.setColunas(this.buildColunaConfig(this.configFiltros || []));
        },
      });
  }

  /** Configura as colunas de totais, utilizadas nos grupos */
  private configurarColunasTotais() {
    // Apenas para garantir que as colunas foram configuradas antes de
    //   tentar montar as colunas de totais
    this.colunasConfiguradas$
      .pipe(
        filter((ok) => ok),
        take(1),
      )
      .subscribe({
        next: () => {
          // Monta colunas do grupo considerando os totais
          const colunasTotais: DataManagerColunaGrupo[] = [];
          let primeiroTotalOk = false;
          this.colunas().forEach((coluna) => {
            // Considera apenas colunas não agrupadas
            if (
              (coluna.tipoColuna === 'dadoAdicional' && coluna.sel && coluna.colunaMaster.config.grupo === undefined) ||
              (coluna.tipoColuna !== 'dadoAdicional' && coluna.config.grupo === undefined)
            ) {
              // A partir da primeira coluna configurada para totalizar, é necessário
              //   criar uma coluna de total, mesmo para as colunas não totalizadas
              if (!primeiroTotalOk && coluna.tipoColuna !== 'dadoAdicional' && coluna.config.total) {
                primeiroTotalOk = true;
              }

              if (primeiroTotalOk) {
                const colunaGrupo = coluna as DataManagerColunaGrupo;
                colunasTotais.push(colunaGrupo);
              }
            }
          });
          this._colunasTotais.set(colunasTotais);
        },
      });
  }

  /** Cria os grupos conforme a configuração existente */
  private configurarGrupos(reset: boolean = false) {
    if (reset) {
      this.gruposPorChave.clear();
    }

    // Apenas para garantir que as colunas foram configuradas antes de
    //   tentar montar os grupos
    this.colunasConfiguradas$
      .pipe(
        filter((ok) => ok),
        take(1),
      )
      .subscribe({
        next: () => {
          const dados = this._dados();

          // Grupos configurados
          const colunas = this.colunas();
          const gruposConfigurados = colunas
            .filter((item) => item.tipoColuna !== 'dadoAdicional' && item.config.grupo !== undefined)
            .sort((a: any, b: any) => a.config.grupo! - b.config.grupo!) as DataManagerColunaGrupo[];
          this._gruposColunas.set(gruposConfigurados);
          if (!gruposConfigurados || gruposConfigurados.length === 0) {
            // Se existiam grupos antes mas não existem mais precisamos
            //   resetar o grupo dos proxies
            if (this._grupos().length > 0) {
              this._dados().forEach((proxy) => {
                proxy.resetGrupo();
              });
            }

            this._grupos.set([]);
            this.configurarOrdenacao();
            return;
          }

          const colunasTotais = colunas.filter(
            (col) => col.tipoColuna !== 'dadoAdicional' && col.config.total,
          ) as DataManagerColunaGrupo[];

          // Se já houverem grupos criados precisamos limpar
          this._grupos().forEach((g) => {
            g.clearProxies();

            for (const coluna of colunasTotais) {
              g.totalReset(coluna);
            }
          });

          // Monta a estrutura dos grupos
          for (const proxy of dados) {
            // Recupera grupo do proxy
            const grupo = this.getGrupoForProxy(proxy);

            // Insere o proxy no último nível do grupo
            proxy.grupo = grupo!;

            // Calcula os totais dos grupos
            for (const coluna of colunasTotais) {
              proxy.totalizarColuna(coluna);
            }
          }

          // Elimina grupos que ficaram vazios
          const eliminaVazios = (gruposNivel: Map<string | number, DataManagerGruposPorChave<T>>) => {
            const entries = gruposNivel.entries();
            for (const entry of entries) {
              const item = entry[1];
              if (item.grupo.ultimoNivel) {
                if (item.grupo.proxies.length === 0) {
                  gruposNivel.delete(entry[0]);
                  item.grupo.pai?.excluirSubrupo(item.grupo);
                }
              } else {
                eliminaVazios(item.subGrupos);
                if (item.subGrupos.size === 0) {
                  gruposNivel.delete(entry[0]);
                  item.grupo.pai?.excluirSubrupo(item.grupo);
                }
              }
            }
          };
          eliminaVazios(this.gruposPorChave);

          const getAllGrupos = (gi: IterableIterator<DataManagerGruposPorChave<T>>) => {
            const result: DataManagerGrupo<T>[] = [];
            for (const item of gi) {
              result.push(item.grupo);
              result.push(...getAllGrupos(item.subGrupos.values()));
            }
            return result;
          };
          this._grupos.set(getAllGrupos(this.gruposPorChave.values()));
          this.configurarOrdenacao();
        },
      });
  }

  private atualizarDados(dados: DataManagerContent<T>[]) {
    this._conteudoOrdenado.set(dados);
    this.atualizarConteudoVisivel();
    this._dadosDisponiveis.set(true);
    this.dadosAtualizados$.next(null);
    this._refreshNecessario.set(true);
  }

  private atualizarConteudoVisivel() {
    const conteudo = this._conteudoOrdenado().filter((item) => item.visivel);

    this._conteudoVisivel.set(conteudo);
  }

  /** Ordena os dados originais e os seus grupos. Como esta é a última etapa do processamento
   * sinaliza também a disponibilidade dos dados
   */
  private configurarOrdenacao() {
    const colunas = this._colunas();
    if (!colunas) {
      return;
    }

    const dadosOrdenar: DataManagerContent<T>[] = [...(this._dados() || [])];

    // Se não houver nem ordenação nem grupos configurados retorna a ordem
    // original dos dados
    const grupos = this._grupos();
    const colunaOrdem = colunas.find((col) => col.config.ordenacao);
    if (!colunaOrdem && grupos.length === 0) {
      this.atualizarDados(dadosOrdenar);
      return;
    }

    // Se não houver ordenação mas houver grupos retorna os dados agrupados
    if (!colunaOrdem && grupos.length > 0) {
      const output = grupos.reduce((prev, curr) => {
        prev.push(curr);
        if (curr.ultimoNivel) {
          prev.push(...curr.proxies);
        }
        return prev;
      }, [] as DataManagerContent<T>[]);

      this.atualizarDados(output);
      return;
    }

    let ordemfunc: (a: any, b: any) => number;
    if (colunaOrdem!.tipoDado !== CriterioTipoDado.valor && colunaOrdem!.tipoDado !== CriterioTipoDado.numerico) {
      ordemfunc = (a: string, b: string) => (a?.localeCompare ? a.localeCompare(b) : 0);
    } else {
      ordemfunc = (a: any, b: any) => (a || 0) - (b || 0);
    }

    // Se houver ordenação mas não houver grupos, ordena os dados originais
    if (this.niveisGrupo === 0) {
      dadosOrdenar.sort((a, b) => {
        const aProxy = a as DataManagerProxy<T>;
        const bProxy = b as DataManagerProxy<T>;
        return (
          ordemfunc(aProxy.get(colunaOrdem!), bProxy.get(colunaOrdem!)) *
          (colunaOrdem!.config.ordenacao === 'asc' ? 1 : -1)
        );
      });

      this.atualizarDados(dadosOrdenar);
      return;
    }

    // Executa a ordenação dentro dos grupos
    const colunaOrdemGrupo = colunaOrdem! as DataManagerColunaGrupo;
    const colunaOrdemConfig = colunaOrdemGrupo.config;
    const colTotal = colunaOrdemConfig.total;
    const ordemChaveGrupo = (colunaOrdemConfig.grupo || 0) > 0;
    const sortNivel = (gruposNivel: DataManagerGrupo<T>[]) => {
      let nivelSorted = [];
      if (colTotal) {
        // Se a ordenação configurada é sobre uma coluna totalizada, precisamos
        // ordenar este grupo usando os totais
        nivelSorted = gruposNivel.sort((a, b) => {
          return (
            ordemfunc(a.getTotal(colunaOrdemGrupo), b.getTotal(colunaOrdemGrupo)) *
            (colunaOrdemConfig.ordenacao === 'asc' ? 1 : -1)
          );
        });
      } else if (ordemChaveGrupo) {
        // Se a ordenação é sobre a chave de um grupo, ordenamos o grupo pela chave
        nivelSorted = gruposNivel.sort((a, b) => {
          return ordemfunc(a.key, b.key) * (colunaOrdemConfig.ordenacao === 'asc' ? 1 : -1);
        });
      } else {
        nivelSorted = gruposNivel;
      }

      // Monta saída com o nível ordenado e seus filhos também ordenados
      const a = nivelSorted.reduce<DataManagerContent<T>[]>((prev, curr) => {
        prev.push(curr);
        if (!curr.ultimoNivel) {
          const nivelOrdenado = sortNivel(curr.subGrupos);
          prev = prev.concat(nivelOrdenado);
        } else {
          const dados = [...curr.proxies];
          dados.sort((a, b) => {
            return (
              ordemfunc(a.get(colunaOrdemGrupo), b.get(colunaOrdemGrupo)) *
              (colunaOrdemConfig.ordenacao === 'asc' ? 1 : -1)
            );
          });
          prev = prev.concat(dados);
        }
        return prev;
      }, []);
      return a;
    };

    // const gruposNivel1 = this._grupos().filter((g) => g.nivel === 1);
    const gruposNivel1 = Array.from(this.gruposPorChave.values()).map((gpc) => gpc.grupo);
    this.atualizarDados(sortNivel(gruposNivel1));
  }

  /** Obtém o valor de um dado adicional */
  private getDadoAdicional(valor: string | number, criterio: string, dadoAdicional: string) {
    const cache = this.daCache.get(criterio);
    if (cache) {
      const daItem = (cache as any)[valor];
      return (daItem && daItem[dadoAdicional]) || '';
    }

    return '';
  }

  /** Retorna o grupo ao qual o proxy pertence. Se o grupo não existir
   * ele será criado na estrutura correta
   */
  private getGrupoForProxy(proxy: DataManagerProxy<T>) {
    const gruposConfigurados = this.gruposColunas();
    const gruposMontagem = this.gruposPorChave;

    if (!gruposConfigurados) {
      return null;
    }

    let grupoMontagemAtual: DataManagerGruposPorChave<T> | undefined;
    let grupoMontagemAnt: DataManagerGruposPorChave<T> | undefined;
    for (const grupoConf of gruposConfigurados) {
      // Recupera a chave deste grupo neste proxy
      let chave = proxy.get(grupoConf, true);
      if (grupoConf.tipoDado === CriterioTipoDado.data) {
        if (typeof chave === 'string') {
          chave = chave.replace(/[^0-9]/g, '').substring(0, 6);
        } else if (chave instanceof Date) {
          chave = `${chave.getFullYear()}${chave.getMonth()}`;
        } else if (chave instanceof DateTime) {
          chave = `${chave.year}${chave.month}`;
        }
      }

      // Recupera o grupo a partir da chave
      grupoMontagemAtual = grupoMontagemAnt ? grupoMontagemAnt.subGrupos.get(chave) : gruposMontagem.get(chave);
      if (!grupoMontagemAtual) {
        const novoGrupo = new DataManagerGrupo<T>(
          chave,
          grupoConf,
          (grupoMontagemAnt?.grupo.nivel || 0) + 1,
          grupoMontagemAnt?.grupo,
        );
        grupoMontagemAtual = {
          subGrupos: new Map<string | number, DataManagerGruposPorChave<T>>(),
          grupo: novoGrupo,
        };
        grupoMontagemAnt
          ? grupoMontagemAnt.subGrupos.set(chave, grupoMontagemAtual)
          : gruposMontagem.set(chave, grupoMontagemAtual);
      }

      grupoMontagemAnt = grupoMontagemAtual;
    }

    return grupoMontagemAtual?.grupo || null;
  }

  /** Obtém o valor de um dado adicional */
  private getLookup(
    lookup: DataManagerControllerLookupItem,
    key: string | number,
    // dadosOriginais: any,
    atributoLookup: string,
  ) {
    const lookupItem = lookup.dados.get(key);
    return lookupItem && lookupItem[atributoLookup];
  }

  private emitConfigChanged() {
    if (!this._colunas()) {
      return;
    }

    const configAjustada = this._colunas()!.reduce<DataManagerColunaConfig[]>((prev, curr) => {
      if (curr.tipoColuna !== 'dadoAdicional') {
        const newColunaConfig: DataManagerColunaConfig = {
          id: curr.id,
          largura: curr.config.largura,
          total: curr.config.total,
          grupo: curr.config.grupo,
          ordenacao: curr.config.ordenacao,
          ocultarColuna: curr.config.ocultarColuna,
        };

        if (curr.tipoColuna === 'criterio' || curr.tipoColuna === 'simples') {
          newColunaConfig.dadosAdicionais = curr.dadosAdicionais.filter((da) => da.sel).map((da) => da.config);
        }

        prev.push(newColunaConfig);
      }

      return prev;
    }, []);
    this._preferenciasChanged$.next({ colunas: configAjustada });
  }
}
